import { type LocationSearchResult } from '@/types/locationSearch';
import { getCurrentLocation } from '@/utils/locationUtils';
import { getUserCountry, clearCountryCache } from '@/utils/countryCache';
import {
  buildPlaceSearchAwayNotice,
  type PlaceSearchCountry,
} from '@/utils/placeSearchNotice';

const liberiaApiBaseUrl = import.meta.env.VITE_LIBERIA_API_BASE_URL?.replace(/\/$/, '');

export interface PlaceLocationSearchOutcome {
  detectedCountry: PlaceSearchCountry | null;
  /** Shown when the query names a country the searcher is not in. */
  notice: string | null;
  error: string | null;
  primary: LocationSearchResult | null;
  alternatives: LocationSearchResult[];
}

function mapLiberiaApiLocationResult(result: {
  latitude: number;
  longitude: number;
  name?: string;
  formattedAddress: string;
  placeId: string;
  types?: string[];
  businessStatus?: string;
  distanceMeters?: number;
}): LocationSearchResult {
  return {
    type: 'location',
    lat: result.latitude,
    lng: result.longitude,
    name: result.name,
    formattedAddress: result.formattedAddress,
    placeId: result.placeId,
    types: result.types || [],
    businessStatus: result.businessStatus,
    distanceMeters: result.distanceMeters,
  };
}

/**
 * Same lookup Place search uses: country-wide named places, nearby categories.
 * Get Directions calls this. Place mode keeps its own copy so that screen stays unchanged.
 */
export async function searchPlacesInCountry(
  searchQuery: string,
  countries: PlaceSearchCountry[],
  detectedCountry: PlaceSearchCountry | null,
  selectedCountry: PlaceSearchCountry | null = null
): Promise<PlaceLocationSearchOutcome> {
  const trimmedQuery = searchQuery.trim();
  let resolvedDetected = detectedCountry;

  let userLocation: { lat: number; lng: number } | null = null;
  let userCountry: { code?: string; name?: string } | null = null;

  try {
    const coords = await getCurrentLocation();
    userLocation = { lat: coords.lat, lng: coords.lng };
    userCountry = await getUserCountry(coords.lat, coords.lng);
    if (userCountry?.code && userCountry?.name) {
      resolvedDetected = { code: userCountry.code, name: userCountry.name };
    }
  } catch {
    // GPS is optional. The API still searches the country we already know.
  }

  // The country on the site wins. GPS only fills in when that country is not set.
  const resolvedCountry =
    selectedCountry?.code && selectedCountry?.name
      ? { code: selectedCountry.code, name: selectedCountry.name }
      : userCountry?.code && userCountry?.name
        ? { code: userCountry.code, name: userCountry.name }
        : resolvedDetected;
  const awayNotice = buildPlaceSearchAwayNotice(trimmedQuery, resolvedCountry, countries);
  if (awayNotice) {
    return {
      detectedCountry: resolvedDetected,
      notice: awayNotice,
      error: null,
      primary: null,
      alternatives: [],
    };
  }

  if (!liberiaApiBaseUrl) {
    return {
      detectedCountry: resolvedDetected,
      notice: null,
      error: 'QLS API is not configured',
      primary: null,
      alternatives: [],
    };
  }

  const response = await fetch(`${liberiaApiBaseUrl}/api/v1/search/locations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: trimmedQuery,
      userLocation,
      userCountry: resolvedCountry?.code || userCountry?.code || userCountry?.name || null,
    }),
  });

  const payload = await response.json();
  if (!response.ok) {
    return {
      detectedCountry: resolvedDetected,
      notice: null,
      error: payload?.error?.message || 'Failed to search locations',
      primary: null,
      alternatives: [],
    };
  }

  if (!payload?.data?.primary) {
    const errorMsg = payload?.error?.message || 'Location not found';
    if (errorMsg.includes('in United States') || errorMsg.includes('country')) {
      clearCountryCache();
    }
    return {
      detectedCountry: resolvedDetected,
      notice: null,
      error: errorMsg,
      primary: null,
      alternatives: [],
    };
  }

  return {
    detectedCountry: resolvedDetected,
    notice: null,
    error: null,
    primary: mapLiberiaApiLocationResult(payload.data.primary),
    alternatives: (payload.data.alternatives || []).map(mapLiberiaApiLocationResult),
  };
}
